import React, { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { Plus, Edit, Trash2, Search, Upload, FileSpreadsheet, X, CheckCircle, XCircle, Printer } from 'lucide-react';
import { toast } from 'sonner';

const Specifications = () => {
  const [activeTab, setActiveTab] = useState('specs');
  const [specs, setSpecs] = useState([]);
  const [qcResults, setQcResults] = useState([]);
  const [qcLogs, setQcLogs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const blankSignee = () => ({ name: '', department: '', designation: '', sign_date: '' });
  const emptyForm = {
    product_sku: '', product_name: '', spec_name: '', version: '1.0', parameters: [],
    spec_type: 'customer', customer_name: '', material_name: '', size: '',
    prepared_by: blankSignee(), reviewed_by: blankSignee(), approved_by: blankSignee(),
    approval_status: 'draft'
  };
  const [formData, setFormData] = useState({ ...emptyForm });
  // A parameter is captured the way it prints: Test Parameter + a free-text
  // Specification (e.g. "Triple laminated bags made of 12 mic Polyester with 12 mic
  // Aluminium foil and further laminate with 110 mic Natural Poly."). The numeric
  // min/target/max are optional and only used to auto-validate QC test results.
  const blankParam = { name: '', specification: '', unit: '', min_value: '', max_value: '', target_value: '', test_method: '' };
  const [paramInput, setParamInput] = useState({ ...blankParam });
  const [showParamLimits, setShowParamLimits] = useState(false);
  const [resultForm, setResultForm] = useState({ product_sku: '', product_name: '', test_results: [], remarks: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sRes, rRes, lRes, iRes] = await Promise.all([
        api.get('/specs/'), api.get('/specs/qc-results'), api.get('/specs/qc-logs'), api.get('/sales/inventory')
      ]);
      setSpecs(sRes.data); setQcResults(rRes.data); setQcLogs(lRes.data); setInventory(iRes.data);
    } catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm, prepared_by: blankSignee(), reviewed_by: blankSignee(), approved_by: blankSignee() }); setShowModal(true); };
  const openEdit = (s) => {
    setEditing(s);
    setFormData({
      product_sku: s.product_sku, product_name: s.product_name || '', spec_name: s.spec_name,
      version: s.version || '1.0', parameters: s.parameters || [],
      spec_type: s.spec_type || 'customer', customer_name: s.customer_name || '', material_name: s.material_name || '', size: s.size || '',
      prepared_by: { ...blankSignee(), ...(s.prepared_by || {}) },
      reviewed_by: { ...blankSignee(), ...(s.reviewed_by || {}) },
      approved_by: { ...blankSignee(), ...(s.approved_by || {}) },
      approval_status: s.approval_status || 'draft'
    });
    setShowModal(true);
  };
  const setSignee = (block, field, val) => setFormData(prev => ({ ...prev, [block]: { ...prev[block], [field]: val } }));

  // The printed SPECIFICATION column is the free-text specification when present;
  // otherwise it falls back to the numeric min / target / max range + unit.
  const specText = (p) => {
    if ((p.specification || '').trim()) return p.specification;
    const range = [p.min_value, p.target_value, p.max_value]
      .filter(v => v !== '' && v !== undefined && v !== null).join(' / ');
    return range ? `${range} ${p.unit || ''}`.trim() : '';
  };

  const printSpec = (s) => {
    const params = (s.parameters || []).map((p, i) => `<tr><td>${i + 1}</td><td>${p.name || ''}</td><td style="white-space:pre-line">${specText(p)}</td></tr>`).join('');
    const signeeCol = (b) => `<td style="text-align:center">${b?.name || ''}<br/><span style="font-size:9px">${b?.department || ''}</span><br/><span style="font-size:9px">${b?.designation || ''}</span><br/><span style="font-size:9px">${b?.sign_date || ''}</span></td>`;
    const w = window.open('', `spec_print_${s.id}`, 'width=900,height=700');
    w.document.open();
    w.document.write(`<html><head><title>${s.spec_name || 'Specification'}</title>
      <style>
        @page{size:A4;margin:14mm}
        body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:16px}
        h1{font-size:16px;margin:0 0 6px;text-align:center}
        table{width:100%;border-collapse:collapse;margin-top:10px}
        th,td{border:1px solid #666;padding:6px;font-size:11px}
        th{background:#1a237e;color:#fff}
        .meta td{border:1px solid #666}
        @media print{button{display:none !important}}
      </style></head><body>
      <h1>${s.spec_type === 'internal' ? 'Internal Specification' : 'Customer Specification'}</h1>
      <table class="meta">
        <tr><td><strong>Prepared By</strong></td>${signeeCol(s.prepared_by)}<td><strong>Reviewed By</strong></td>${signeeCol(s.reviewed_by)}<td><strong>Approved By</strong></td>${signeeCol(s.approved_by)}</tr>
      </table>
      <table class="meta" style="margin-top:8px">
        <tr><td style="width:120px"><strong>Customer:</strong></td><td>${s.customer_name || ''}</td></tr>
        <tr><td><strong>Material Name:</strong></td><td>${s.material_name || s.product_name || ''}</td></tr>
        <tr><td><strong>Size:</strong></td><td>${s.size || ''}</td></tr>
      </table>
      <table><thead><tr><th style="width:50px">S.No</th><th>Test Parameter</th><th>Specification</th></tr></thead>
      <tbody>${params || '<tr><td></td><td></td><td></td></tr>'}</tbody></table>
      <button onclick="window.print()" style="margin-top:16px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/specs/${editing.id}`, formData);
      else await api.post('/specs/', formData);
      setShowModal(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteSpec = async (id, name) => {
    if (!window.confirm(`Delete spec "${name}"?`)) return;
    try { await api.delete(`/specs/${id}`); fetchData(); } catch(e) { toast.error('Failed'); }
  };

  const addParam = () => {
    if (!paramInput.name) return;
    setFormData({ ...formData, parameters: [...formData.parameters, { ...paramInput }] });
    setParamInput({ ...blankParam });
  };

  const updateParam = (idx, field, val) => {
    const parameters = formData.parameters.map((p, i) => (i === idx ? { ...p, [field]: val } : p));
    setFormData({ ...formData, parameters });
  };

  const removeParam = (idx) => { setFormData({ ...formData, parameters: formData.parameters.filter((_, i) => i !== idx) }); };

  const onProductSelect = (sku) => {
    const inv = inventory.find(i => i.sku === sku);
    setFormData({ ...formData, product_sku: sku, product_name: inv?.name || '' });
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/specs/import-excel', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.error(`Imported ${res.data.imported_count} specifications`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  // QC Test Result
  const openQcResult = (spec) => {
    const testParams = (spec?.parameters || []).map(p => ({ name: p.name, specification: specText(p), unit: p.unit, target_value: p.target_value, min_value: p.min_value, max_value: p.max_value, actual_value: '', status: 'pending' }));
    setResultForm({ product_sku: spec?.product_sku || '', product_name: spec?.product_name || '', test_results: testParams, remarks: '' });
    setShowResultModal(true);
  };

  const submitQcResult = async (e) => {
    e.preventDefault();
    try {
      await api.post('/specs/qc-results', resultForm);
      setShowResultModal(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const filtered = specs.filter(s => (s.product_sku || '').toLowerCase().includes(searchTerm.toLowerCase()) || (s.spec_name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (s.product_name || '').toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div data-testid="specifications-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Specifications & QC</h1>
      </div>

      <div className="flex gap-2 mb-4">
        {[{ key: 'specs', label: 'Specifications' }, { key: 'results', label: 'QC Test Results' }, { key: 'logs', label: 'QC Logs' }].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} className={`btn btn-sm ${activeTab === t.key ? 'btn-primary' : 'btn-secondary'}`} data-testid={`tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'specs' && (
        <>
          <div className="card mb-4">
            <div className="flex gap-4" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
                <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
                <input type="text" className="form-input" placeholder="Search specs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-specs" />
              </div>
              <input type="file" ref={fileInputRef} accept=".xlsx,.xls" onChange={handleExcelUpload} style={{ display: 'none' }} />
              <button onClick={() => fileInputRef.current?.click()} className="btn btn-secondary btn-sm" disabled={uploading} data-testid="import-excel-btn"><Upload size={14} /> {uploading ? 'Importing...' : 'Import Excel'}</button>
              <button onClick={openCreate} className="btn btn-primary btn-sm" data-testid="add-spec-btn"><Plus size={16} /> Add Spec</button>
            </div>
          </div>
          <div className="card">
            {loading ? <div>Loading...</div> : (
              <div className="table-container">
                <table>
                  <thead><tr><th>Product SKU</th><th>Product Name</th><th>Spec Name</th><th>Type</th><th>Version</th><th>Parameters</th><th>Status</th><th>Actions</th></tr></thead>
                  <tbody>
                    {filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No specs found</td></tr> : filtered.map(s => (
                      <tr key={s.id} data-testid={`spec-row-${s.id}`}>
                        <td className="font-mono">{s.product_sku}</td>
                        <td>{s.product_name}</td>
                        <td style={{ fontWeight: 500 }}>{s.spec_name}</td>
                        <td><span className={`badge ${s.spec_type === 'internal' ? 'badge-warning' : 'badge-info'}`}>{s.spec_type === 'internal' ? 'Internal' : 'Customer'}</span>{s.spec_type === 'internal' && s.approval_status ? <div className="text-xs text-muted" style={{ marginTop: 2 }}>{s.approval_status.replace(/_/g, ' ')}</div> : null}</td>
                        <td>{s.version}</td>
                        <td>{(s.parameters || []).length} params</td>
                        <td><span className={`badge ${s.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{s.status}</span></td>
                        <td>
                          <div className="flex gap-2">
                            <button onClick={() => openQcResult(s)} className="btn btn-ghost btn-sm" title="Run QC Test" data-testid={`qc-btn-${s.id}`}><FileSpreadsheet size={14} color="#0047AB" /></button>
                            <button onClick={() => printSpec(s)} className="btn btn-ghost btn-sm" title="Print"><Printer size={14} /></button>
                            <button onClick={() => openEdit(s)} className="btn btn-ghost btn-sm"><Edit size={14} /></button>
                            <button onClick={() => deleteSpec(s.id, s.spec_name)} className="btn btn-ghost btn-sm"><Trash2 size={14} color="#DC2626" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'results' && (
        <div className="card">
          {loading ? <div>Loading...</div> : (
            <div className="table-container">
              <table>
                <thead><tr><th>Product</th><th>Spec</th><th>Tests</th><th>Result</th><th>Tested By</th><th>Date</th></tr></thead>
                <tbody>
                  {qcResults.length === 0 ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No test results</td></tr> : qcResults.map(r => (
                    <tr key={r.id} data-testid={`result-row-${r.id}`}>
                      <td><div className="font-mono">{r.product_sku}</div><div className="text-xs text-muted">{r.product_name}</div></td>
                      <td>{r.spec_name || '-'}</td>
                      <td>
                        <div className="flex gap-1">
                          {(r.test_results || []).map((t, i) => (
                            <span key={i} className={`badge ${t.status === 'pass' ? 'badge-success' : 'badge-danger'}`} style={{ fontSize: '10px' }}>{t.name}</span>
                          ))}
                        </div>
                      </td>
                      <td><span className={`badge ${r.overall_result === 'pass' ? 'badge-success' : 'badge-danger'}`}>{r.overall_result}</span></td>
                      <td>{r.tested_by_name}</td>
                      <td className="text-sm">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="card">
          {loading ? <div>Loading...</div> : (
            <div className="table-container">
              <table>
                <thead><tr><th>Type</th><th>Number/Product</th><th>Status/Result</th><th>Items/Params</th><th>Date</th></tr></thead>
                <tbody>
                  {qcLogs.length === 0 ? <tr><td colSpan={5} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No QC logs</td></tr> : qcLogs.map((l, i) => (
                    <tr key={i}>
                      <td><span className={`badge ${l.type === 'inspection' ? 'badge-info' : 'badge-warning'}`}>{l.type === 'inspection' ? 'Inspection' : 'Test Result'}</span></td>
                      <td className="font-mono">{l.number || l.product_sku || '-'}</td>
                      <td><span className={`badge ${(l.status === 'passed' || l.result === 'pass') ? 'badge-success' : 'badge-danger'}`}>{l.status || l.result}</span></td>
                      <td>{l.items_count || l.params_count || '-'}</td>
                      <td className="text-sm">{l.date ? new Date(l.date).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Spec Modal */}
      {showModal && (
        <div className="modal-overlay" data-testid="spec-modal">
          <div className="modal-content" style={{ maxWidth: '700px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Specification' : 'Create Specification'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2">
                  <div className="form-group">
                    <label className="form-label">Product *</label>
                    <select className="form-select" value={formData.product_sku} onChange={(e) => onProductSelect(e.target.value)} required data-testid="spec-product-select">
                      <option value="">Select Product</option>
                      {inventory.map(i => <option key={i.sku} value={i.sku}>{i.sku} - {i.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Spec Name *</label><input type="text" className="form-input" value={formData.spec_name} onChange={(e) => setFormData({...formData, spec_name: e.target.value})} required data-testid="spec-name" /></div>
                </div>
                <div className="grid grid-cols-2">
                  <div className="form-group"><label className="form-label">Version</label><input type="text" className="form-input" value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} style={{ width: '100px' }} /></div>
                  <div className="form-group">
                    <label className="form-label">Specification Type</label>
                    <select className="form-select" value={formData.spec_type} onChange={(e) => setFormData({ ...formData, spec_type: e.target.value })} data-testid="spec-type">
                      <option value="customer">Customer Spec</option>
                      <option value="internal">Internal Spec (Hridya Pack)</option>
                    </select>
                  </div>
                </div>

                {formData.spec_type === 'internal' && (
                  <div style={{ marginTop: 8, padding: '12px', background: 'var(--bg-surface-alt, #f8fafc)', borderRadius: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Internal Specification Format</div>
                    <div className="grid grid-cols-3" style={{ gap: 10 }}>
                      <div className="form-group"><label className="form-label">Customer</label><input type="text" className="form-input" value={formData.customer_name} onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })} /></div>
                      <div className="form-group"><label className="form-label">Material Name</label><input type="text" className="form-input" value={formData.material_name} onChange={(e) => setFormData({ ...formData, material_name: e.target.value })} /></div>
                      <div className="form-group"><label className="form-label">Size</label><input type="text" className="form-input" value={formData.size} onChange={(e) => setFormData({ ...formData, size: e.target.value })} /></div>
                    </div>
                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginTop: 6 }}>
                      <thead><tr><th style={{ padding: 4 }}></th><th style={{ padding: 4 }}>Prepared By</th><th style={{ padding: 4 }}>Reviewed By</th><th style={{ padding: 4 }}>Approved By</th></tr></thead>
                      <tbody>
                        {['name', 'department', 'designation', 'sign_date'].map(field => (
                          <tr key={field}>
                            <td style={{ padding: 4, fontWeight: 600, textTransform: 'capitalize' }}>{field.replace('_', ' & ')}</td>
                            {['prepared_by', 'reviewed_by', 'approved_by'].map(block => (
                              <td key={block} style={{ padding: 4 }}>
                                <input type={field === 'sign_date' ? 'date' : 'text'} className="form-input" value={formData[block][field]} onChange={(e) => setSignee(block, field, e.target.value)} style={{ fontSize: 12, padding: 4 }} data-testid={`${block}-${field}`} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="form-group" style={{ marginTop: 8 }}>
                      <label className="form-label">Approval Status</label>
                      <select className="form-select" value={formData.approval_status} onChange={(e) => setFormData({ ...formData, approval_status: e.target.value })} data-testid="approval-status">
                        <option value="draft">Draft</option>
                        <option value="sent_for_customer_approval">Sent for Customer Approval</option>
                        <option value="customer_approved">Customer Approved</option>
                        <option value="approved">Finally Approved (Internal)</option>
                      </select>
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '12px' }}>
                  <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                    <label className="form-label" style={{ fontWeight: 600, margin: 0 }}>Parameters</label>
                    <label style={{ fontSize: 11, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="checkbox" checked={showParamLimits} onChange={(e) => setShowParamLimits(e.target.checked)} data-testid="toggle-param-limits" />
                      Show unit / min / target / max / method (QC auto-validation)
                    </label>
                  </div>
                  <div className="flex gap-2 mb-2" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <input type="text" className="form-input" placeholder="Test Parameter (e.g. Description)" value={paramInput.name} onChange={(e) => setParamInput({...paramInput, name: e.target.value})} style={{ flex: '1 1 200px' }} data-testid="param-name" />
                    <textarea className="form-textarea" placeholder="Specification (e.g. Triple laminated bags made of 12 mic Polyester with 12 mic Aluminium foil and further laminate with 110 mic Natural Poly.)" value={paramInput.specification} onChange={(e) => setParamInput({...paramInput, specification: e.target.value})} rows={2} style={{ flex: '2 1 320px' }} data-testid="param-specification" />
                    <button type="button" onClick={addParam} className="btn btn-secondary btn-sm" data-testid="add-param-btn"><Plus size={14} /></button>
                  </div>
                  {showParamLimits && (
                    <div className="flex gap-2 mb-2" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <input type="text" className="form-input" placeholder="Unit" value={paramInput.unit} onChange={(e) => setParamInput({...paramInput, unit: e.target.value})} style={{ flex: 1 }} />
                      <input type="text" className="form-input" placeholder="Min" value={paramInput.min_value} onChange={(e) => setParamInput({...paramInput, min_value: e.target.value})} style={{ flex: 1 }} />
                      <input type="text" className="form-input" placeholder="Target" value={paramInput.target_value} onChange={(e) => setParamInput({...paramInput, target_value: e.target.value})} style={{ flex: 1 }} />
                      <input type="text" className="form-input" placeholder="Max" value={paramInput.max_value} onChange={(e) => setParamInput({...paramInput, max_value: e.target.value})} style={{ flex: 1 }} />
                      <input type="text" className="form-input" placeholder="Method" value={paramInput.test_method} onChange={(e) => setParamInput({...paramInput, test_method: e.target.value})} style={{ flex: 1 }} />
                    </div>
                  )}
                  {formData.parameters.length > 0 && (
                    <div className="table-container" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                      <table>
                        <thead><tr><th style={{ width: 50 }}>S.No</th><th style={{ width: '30%' }}>Test Parameter</th><th>Specification</th>{showParamLimits && <><th>Unit</th><th>Min</th><th>Target</th><th>Max</th><th>Method</th></>}<th></th></tr></thead>
                        <tbody>
                          {formData.parameters.map((p, i) => (
                            <tr key={i}>
                              <td className="tabular-nums">{i + 1}</td>
                              <td><input type="text" className="form-input" value={p.name || ''} onChange={(e) => updateParam(i, 'name', e.target.value)} style={{ fontSize: 12, padding: 4 }} /></td>
                              <td><textarea className="form-textarea" value={p.specification || ''} onChange={(e) => updateParam(i, 'specification', e.target.value)} rows={2} style={{ fontSize: 12, padding: 4 }} data-testid={`param-spec-${i}`} /></td>
                              {showParamLimits && <>
                                <td><input type="text" className="form-input" value={p.unit || ''} onChange={(e) => updateParam(i, 'unit', e.target.value)} style={{ fontSize: 12, padding: 4, width: 60 }} /></td>
                                <td><input type="text" className="form-input" value={p.min_value || ''} onChange={(e) => updateParam(i, 'min_value', e.target.value)} style={{ fontSize: 12, padding: 4, width: 60 }} /></td>
                                <td><input type="text" className="form-input" value={p.target_value || ''} onChange={(e) => updateParam(i, 'target_value', e.target.value)} style={{ fontSize: 12, padding: 4, width: 60 }} /></td>
                                <td><input type="text" className="form-input" value={p.max_value || ''} onChange={(e) => updateParam(i, 'max_value', e.target.value)} style={{ fontSize: 12, padding: 4, width: 60 }} /></td>
                                <td><input type="text" className="form-input" value={p.test_method || ''} onChange={(e) => updateParam(i, 'test_method', e.target.value)} style={{ fontSize: 12, padding: 4, width: 80 }} /></td>
                              </>}
                              <td><button type="button" onClick={() => removeParam(i)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-spec-btn">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QC Test Result Modal */}
      {showResultModal && (
        <div className="modal-overlay" data-testid="qc-result-modal">
          <div className="modal-content" style={{ maxWidth: '650px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>QC Test - {resultForm.product_sku}</h2></div>
            <form onSubmit={submitQcResult}>
              <div className="modal-body">
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>Enter actual values. Results will be auto-validated against spec ranges.</p>
                <div className="table-container">
                  <table>
                    <thead><tr><th style={{ width: 40 }}>S.No</th><th>Test Parameter</th><th>Specification</th><th>Unit</th><th>Actual Value *</th></tr></thead>
                    <tbody>
                      {resultForm.test_results.map((t, i) => (
                        <tr key={i}>
                          <td className="tabular-nums">{i + 1}</td>
                          <td style={{ fontWeight: 500 }}>{t.name}</td>
                          <td style={{ whiteSpace: 'pre-line', fontSize: 11 }}>{t.specification || [t.min_value, t.target_value, t.max_value].filter(v => v !== '' && v != null).join(' / ')}</td>
                          <td>{t.unit}</td>
                          <td>
                            <input type="text" className="form-input" value={t.actual_value} onChange={(e) => {
                              const updated = [...resultForm.test_results];
                              updated[i].actual_value = e.target.value;
                              setResultForm({ ...resultForm, test_results: updated });
                            }} required style={{ width: '100px' }} data-testid={`actual-${t.name}`} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="form-group" style={{ marginTop: '12px' }}><label className="form-label">Remarks</label><textarea className="form-textarea" value={resultForm.remarks} onChange={(e) => setResultForm({...resultForm, remarks: e.target.value})} rows={2} /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowResultModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-qc-result-btn">Submit Test Result</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Specifications;
